import React from 'react';
import type { ColumnsType } from 'antd/es/table';
import { BondFamily, CatalogueRow, FilterOption } from './types';

const labelOrder = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

// One presentation-only column set for grouped instruments and concrete issues.
export function instrumentColumns<T>({
  options,
  members,
  family,
  ticker,
  name,
  onOpen,
  canOpen,
  showAssetClass = true,
  defaultExposureSort = false,
  extraColumns = [],
}: {
  options: FilterOption[];
  members: (record: T) => CatalogueRow[];
  family?: (record: T) => BondFamily | null;
  ticker?: (record: T) => string;
  name?: (record: T) => string;
  onOpen?: (record: T) => void;
  canOpen?: (record: T) => boolean;
  showAssetClass?: boolean;
  defaultExposureSort?: boolean;
  extraColumns?: ColumnsType<T>;
}): ColumnsType<T> {
  const labels = new Map(options.map(option => [option.key, option.label]));
  const values = (record: T, value: (row: CatalogueRow) => string) =>
    [...new Set(members(record).map(value).filter(Boolean))].join(', ') || '—';
  const facet = (record: T, key: string) =>
    [
      ...new Set(
        members(record).flatMap(row =>
          (row.facets[key] || []).map(value => labels.get(value) || ''),
        ),
      ),
    ]
      .filter(Boolean)
      .join(', ');
  const exposure = (record: T) => facet(record, 'exposure') || 'Unclassified';
  const assetClass = (record: T) =>
    values(record, row => row.resourcetype || '');
  const tickerLabel = (record: T) =>
    ticker?.(record) ||
    family?.(record)?.code ||
    members(record)[0]?.ticker ||
    '—';
  const nameLabel = (record: T) =>
    name?.(record) ||
    (family?.(record) ? 'Synthetic bond family' : members(record)[0]?.name) ||
    '—';
  const columns: ColumnsType<T> = [
    {
      title: 'Ticker',
      key: 'ticker',
      sorter: (left, right) =>
        labelOrder.compare(tickerLabel(left), tickerLabel(right)),
      render: (_, record) => {
        const label = tickerLabel(record);
        return onOpen && (!canOpen || canOpen(record)) ? (
          <button
            className="ac-instrument-link"
            title={label}
            onClick={() => onOpen(record)}
          >
            {label}
          </button>
        ) : (
          label
        );
      },
    },
    {
      title: 'Name',
      key: 'name',
      sorter: (left, right) =>
        labelOrder.compare(nameLabel(left), nameLabel(right)),
      render: (_, record) => nameLabel(record),
    },
    {
      title: 'Exposure',
      key: 'exposure',
      defaultSortOrder: defaultExposureSort ? 'ascend' : undefined,
      sorter: (left, right) =>
        labelOrder.compare(exposure(left), exposure(right)),
      render: (_, record) => exposure(record),
    },
    {
      title: 'Expression form',
      key: 'form',
      render: (_, record) => facet(record, 'form') || '—',
    },
    {
      title: 'Asset class',
      key: 'resourcetype',
      sorter: (left, right) =>
        labelOrder.compare(assetClass(left), assetClass(right)),
      render: (_, record) => assetClass(record),
    },
    {
      title: 'CCY',
      key: 'currency',
      render: (_, record) => values(record, row => row.currency),
    },
    {
      title: 'Geography',
      key: 'listing_geography',
      render: (_, record) => values(record, row => row.listing_geography || ''),
    },
    ...extraColumns,
    {
      title: 'Desk Role',
      key: 'role',
      className: 'ac-desk-role',
      ellipsis: true,
      render: (_, record) => values(record, row => row.primary_role),
    },
  ];
  return showAssetClass
    ? columns
    : columns.filter(column => column.key !== 'resourcetype');
}
